import { useSelector } from 'react-redux';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, Check, X } from 'lucide-react';
import { useSidebar } from '../ui/sidebar';
import { isTouchViewport } from '@/hooks/use-mobile';
import { LIVE_GUIDES, PHONE_NEW_CONVERSATION, type LiveGuideStep } from './catalogue';
import { useWalkthroughs } from './WalkthroughProvider';

/** Targets are data-onboarding names, or CSS selectors when they start with "[". */
const selectorFor = (name: string) => name.startsWith('[') ? name : `[data-onboarding="${name}"]`;
const PUSHED_PAGE_BACK = '[role="dialog"][aria-modal="true"] button[aria-label^="Back"]';

/** Phones: a pushed screen (a team chat's info) covers the page without hiding it from VoiceOver. */
function isUnderPushedPage(node: HTMLElement, rect: DOMRect) {
  const hit = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2)?.closest('[aria-modal="true"]');
  return !!hit && !hit.contains(node);
}

function visibleTarget(names: string[], isMobile = false): HTMLElement | null {
  for (const name of names) for (const node of document.querySelectorAll<HTMLElement>(selectorFor(name))) {
    const rect = node.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0 && getComputedStyle(node).visibility !== 'hidden' && !node.closest('[aria-hidden="true"]') && !(isMobile && isUnderPushedPage(node, rect))) return node;
  }
  return null;
}

/**
 * Whether something other than the guide card is on top of a node's centre:
 * a sticky footer, the edge of the scroll area it sits in, or a panel.
 */
function isCovered(node: HTMLElement) {
  const rect = node.getBoundingClientRect();
  const x = rect.left + rect.width / 2, y = rect.top + rect.height / 2;
  if (!rect.width || !rect.height || x < 0 || y < 0 || x >= innerWidth || y >= innerHeight) return false;
  const top = document.elementsFromPoint(x, y).find(element => !element.closest('[data-onboarding-guide]'));
  return !!top && !node.contains(top);
}

/** A target that is on the page but hidden by a sheet, menu or the drawer opened over it (Radix marks what it hides). */
function behindModalLayer(names: string[]) {
  return names.some(name => [...document.querySelectorAll<HTMLElement>(selectorFor(name))].some(node => node.getBoundingClientRect().width > 0 && !!node.closest('[data-aria-hidden="true"]')));
}

type ShownStep = Omit<LiveGuideStep, 'phone'> & { openDrawer?: boolean };
/** The step as shown: phones swap in its phone targets, copy and click. */
const showStep = (step: LiveGuideStep | undefined, isMobile: boolean): ShownStep | undefined =>
  step && (isMobile && step.phone ? { ...step, ...step.phone } : step);
const clickMatches = (click: ShownStep['click'], target: Element, name: string | undefined) =>
  (Array.isArray(click) ? click : click ? [click] : []).some(item => item.startsWith('[') ? !!target.closest(item) : item === name);

interface Position { target: HTMLElement | null; host: HTMLElement; inline: boolean; x: number; y: number; width: number; recovery: string | null }

/**
 * The area the card may use. Phones keep it clear of the notch, the home
 * indicator, the tab bar and the keyboard; elsewhere it is the window.
 */
function usableArea(isMobile: boolean) {
  if (!isMobile) return { left: 0, top: 0, right: innerWidth, bottom: innerHeight };
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;top:var(--safe-top);left:var(--safe-left);right:var(--safe-right);height:calc(var(--app-height) - var(--safe-top) - var(--mobile-tabbar-height))';
  document.body.appendChild(probe);
  const rect = probe.getBoundingClientRect();
  probe.remove();
  return { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom };
}

export function WalkthroughCoach() {
  const { guide } = useWalkthroughs();
  return guide ? <ActiveCoach key={guide.runId} /> : null;
}

function ActiveCoach() {
  const { guide, busy, error, finish, activationPending } = useWalkthroughs();
  const staging = useSelector((state: { auth?: { selfServeSignup?: { environment?: string } } }) => state.auth?.selfServeSignup?.environment === 'staging');
  const task = LIVE_GUIDES.find(item => item.id === guide!.id)!;
  const [index, setIndex] = useState(0);
  const { isMobile, setOpenMobile } = useSidebar();
  const step = useMemo(() => showStep(task.steps[index], isMobile)!, [task, index, isMobile]);
  // Touch screens say tap where desktop says click: phones, and below 1280px
  // iPads and phones in landscape, which keep the desktop layout.
  const touch = isMobile || (innerWidth < 1280 && isTouchViewport());
  const tapOnPhone = (text: string) => touch ? text.replace(/\bClick\b/g, 'Tap').replace(/\bclick\b/g, 'tap') : text;
  const stepBody = tapOnPhone(!isMobile && touch && step.touchBody ? step.touchBody : step.body);
  const body = staging && task.id === 'conversation' && step.final
    ? 'Try writing a message below. This is a staging test account, so customer messages are not sent. You can finish this guide without sending anything.'
    : activationPending && task.id === 'conversation' && step.final
    ? 'Choose a customer in the To field and try writing a message below. Sending stays off until your number is approved and ready and your first payment succeeds. You can finish this guide without sending anything.'
    : stepBody;
  const [position, setPosition] = useState<Position | null>(null);
  const [height, setHeight] = useState(260);
  const card = useRef<HTMLDivElement>(null);
  // The target of a card shown inside its dialog, kept across re-measures.
  const inlineTarget = useRef<HTMLElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { if (isMobile) setOpenMobile(false); }, [location.pathname, isMobile, setOpenMobile]);
  // Phones: steps about the More drawer open it, as the sidebar is always open on desktop.
  useEffect(() => { if (step.openDrawer) setOpenMobile(true); }, [index, step.openDrawer, setOpenMobile]);
  useEffect(() => {
    const click = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target?.closest) return;
      const name = target.closest<HTMLElement>('[data-onboarding]')?.dataset.onboarding;
      if (clickMatches(step.click, target, name)) setIndex(Math.min(index + 1, task.steps.length - 1));
      // Phones: a tap that a later step asks for (Settings in the More drawer
      // while the guide still says "Open More") moves the guide past it.
      else if (isMobile) {
        const later = task.steps.findIndex((item, i) => i > index && clickMatches(showStep(item, true)!.click, target, name));
        if (later > index) setIndex(Math.min(later + 1, task.steps.length - 1));
      }
      if (isMobile && name && ['inbox', 'contacts', 'campaigns', 'user-settings', 'settings-team', 'settings-google', 'teams-groups'].includes(name)) setTimeout(() => setOpenMobile(false), 0);
    };
    document.addEventListener('click', click, true);
    return () => document.removeEventListener('click', click, true);
  }, [step, index, task.steps, isMobile, setOpenMobile]);

  useLayoutEffect(() => {
    if (!card.current) return;
    const observer = new ResizeObserver(() => setHeight(card.current?.getBoundingClientRect().height || 260));
    observer.observe(card.current);
    return () => observer.disconnect();
  }, [position?.host, position?.inline]);

  useLayoutEffect(() => {
    let frame = 0;
    let settle = 0;
    let highlighted: HTMLElement | null = null;
    let scrolledUp: HTMLElement | null = null;
    const measure = () => {
      frame = 0;
      let target = visibleTarget(step.targets, isMobile);
      // A card placed inside a short dialog (landscape phones) can squeeze the
      // form it explains to nothing; keep that form rather than flicker
      // between the two.
      const squeezed = inlineTarget.current;
      if (!target && squeezed?.isConnected && card.current?.parentElement?.hasAttribute('data-onboarding-guide-slot') && squeezed.closest('[role="dialog"]')?.contains(card.current) && step.targets.some(name => squeezed.matches(selectorFor(name)))) target = squeezed;
      let recovery: string | null = null;
      // Phones: while a sheet or the drawer is open over the target, the card
      // steps aside and comes back when it closes.
      if (!target && isMobile && behindModalLayer(step.targets)) {
        highlighted?.removeAttribute('data-walkthrough-highlight'); highlighted = null;
        setPosition(null);
        return;
      }
      if (!target && step.openDrawer) recovery = 'This part of the guide is in the More menu.';
      else if (!target) {
        const fallbacks = task.id === 'conversation'
          ? [['inbox-new-conversation', 'Click the highlighted button to open a new conversation.'], ...(isMobile ? [[PHONE_NEW_CONVERSATION, 'Tap the highlighted button to open a new conversation.']] : []), ['inbox-start-chat', 'Click Start new chat to open a new conversation.'], ['inbox', 'Click Inbox to continue this guide.'], ['mobile-navigation', 'Open the navigation menu to find Inbox.']]
          : task.id === 'campaign'
          ? [...(index >= 2 ? [[index === 3 ? 'campaign-message-step' : index >= 4 ? 'campaign-review-step' : 'campaign-customers-step', 'Choose the highlighted campaign step to continue.']] : []), ...(index >= 1 ? [['campaign-new', isMobile ? 'Tap + to reopen the campaign builder.' : 'Click New campaign to reopen the campaign builder.']] : []), ['campaigns', 'Click Campaigns to continue.'], ['more-features', 'Open More Features, then choose Campaigns.'], ['mobile-navigation', isMobile ? 'Tap More to find Campaigns.' : 'Open the navigation menu to find Campaigns.']]
          : task.id === 'team_chat'
          ? [
            ...(isMobile && (index === 6 || index === 7) ? [[PUSHED_PAGE_BACK, 'Tap Back to return to the conversation.']] : []),
            ...(index === 5 ? [['teams-details', 'Click the highlighted button to show this conversation’s notes.']] : []),
            ...(index >= 3 && index <= 7 ? [['teams-conversation', 'Open one of your conversations to continue.'], ['teams-new-chat', 'You don’t have a conversation yet. Start one with New chat, or end the guide and come back later.']] : []),
            ['teams-groups', isMobile ? 'Tap Team in the tab bar to continue.' : 'Click Groups in the sidebar to continue.'], ['mobile-navigation', isMobile ? 'Tap More to find Groups.' : 'Open the navigation menu to find Groups.'],
          ]
          : ['contact', 'import_contacts'].includes(task.id)
          ? [['contacts-new', isMobile ? 'Tap + to reopen the contact form.' : 'Click New to reopen the contact form.'], ['contacts', 'Click Contacts to continue.'], ['mobile-navigation', 'Open the navigation menu to find Contacts.']]
          : [
            ...(task.id === 'team' && index >= 3 ? [['team-add-user', 'Click Add New User to reopen the team form.']] : []),
            ...(index >= 2 ? [[task.id === 'team' ? 'settings-team' : 'settings-google', `Choose ${task.id === 'team' ? 'Team' : 'Google'} in Settings to continue.`], ['settings-navigation', 'Go back to the settings list to find the highlighted section.']] : []),
            // The account menu in the phone drawer has no Settings row.
            ['user-settings', 'Choose Settings to continue the guide.'], ...(isMobile ? [] : [['user-menu', 'Open your account menu to find Settings.']]), ['mobile-navigation', isMobile ? 'Tap More to find Settings.' : 'Open the navigation menu to find your account menu.'],
          ];
        for (const [name, text] of fallbacks) if ((target = visibleTarget([name], isMobile))) { recovery = tapOnPhone(text); break; }
        if (!target) recovery = 'Return to your dashboard navigation to continue, or end the guide and come back anytime.';
      }
      const area = usableArea(isMobile);
      const modal = target?.closest<HTMLElement>('[role="dialog"]');
      const menu = target?.closest<HTMLElement>('[role="menu"]');
      const slot = modal?.querySelector<HTMLElement>(`[data-onboarding-guide-slot="${task.id}"]`);
      inlineTarget.current = slot ? target : null;
      if (highlighted !== target) {
        highlighted?.removeAttribute('data-walkthrough-highlight'); highlighted = target;
        target?.setAttribute('data-walkthrough-highlight', 'true');
        // Below 1280px an inline card's top stays in view rather than the end
        // of a long form under it; phones also keep targets clear of the tab bar.
        const shown = slot && innerWidth < 1280 ? slot : target;
        const reveal = () => {
          const rect = shown?.getBoundingClientRect();
          if (!shown || !rect) return;
          if (rect.top < 20 || rect.bottom > area.bottom || rect.left < 0 || rect.right > innerWidth) shown.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
          // Below 1280px a target inside the window can still be cut off by the
          // area it scrolls in (the More drawer on small phones) or sit under a
          // sticky footer (the landscape rail); scroll it clear of both.
          else if (innerWidth < 1280 && isCovered(shown)) {
            // Room for the highlight ring, which sits 6px outside the target.
            shown.style.scrollMargin = '8px';
            shown.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
            if (isCovered(shown)) shown.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' });
            shown.style.scrollMargin = '';
          }
        };
        reveal();
        // A screen that is still sliding in or loading rows can move it again.
        window.clearTimeout(settle);
        if (innerWidth < 1280) settle = window.setTimeout(() => { if (highlighted === target) reveal(); }, 600);
      }
      const rect = target?.getBoundingClientRect();
      const host = slot || menu?.parentElement || modal || document.body;
      const hostRect = host === document.body ? null : host.getBoundingClientRect();
      let width = Math.max(240, Math.min(340, area.right - area.left - 28, modal ? modal.clientWidth - 24 : Infinity));
      let x = rect ? rect.right + 16 : area.right - width - 20;
      let y = rect ? rect.top : area.bottom - height - 20;
      if (x + width > area.right - 14) { x = rect ? rect.left : area.left + 14; y = rect ? rect.bottom + 16 : y; }
      if (y + height > area.bottom - 14) y = rect ? rect.top - height - 16 : area.bottom - height - 14;
      x = Math.max(area.left + 14, Math.min(x, area.right - width - 14));
      y = Math.max(area.top + 14, Math.min(y, area.bottom - height - 14));
      // Below 1280px the window can be too short for the card above or below
      // the target (landscape phones); then it goes beside it rather than over it.
      if (rect && innerWidth < 1280 && y < rect.bottom && y + height > rect.top && x < rect.right && x + width > rect.left) {
        const before = Math.max(area.left + 14, rect.left - width - 16);
        const after = Math.min(area.right - width - 14, rect.right + 16);
        const roomBefore = rect.left - area.left - 30, roomAfter = area.right - rect.right - 30;
        if (before + width <= rect.left) x = before;
        else if (after >= rect.right) x = after;
        // A narrower card still fits beside it (a wide target on a landscape phone).
        else if (Math.max(roomBefore, roomAfter) >= 240) {
          width = Math.max(roomBefore, roomAfter);
          x = roomBefore >= roomAfter ? area.left + 14 : rect.right + 16;
        } else {
          // No room beside it either (small phones): cover as little of it as possible.
          const overlap = (top: number) => Math.max(0, Math.min(top + height, rect.bottom) - Math.max(top, rect.top));
          const low = area.bottom - height - 14;
          if (overlap(low) < overlap(y)) y = low;
        }
      }
      // Small phones: when the card fits neither above, below nor beside the
      // target, scroll the target to the top of its scroll area (once) so the
      // card can sit under it instead of covering it.
      if (rect && target && !slot && innerWidth < 1280 && scrolledUp !== target && y < rect.bottom && y + height > rect.top && x < rect.right && x + width > rect.left && rect.height + height + 44 <= area.bottom - area.top) {
        scrolledUp = target;
        target.style.scrollMargin = '14px';
        target.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'instant' });
        target.style.scrollMargin = '';
        schedule();
        return;
      }
      if (hostRect) { x -= hostRect.left - host.scrollLeft; y -= hostRect.top - host.scrollTop; }
      const next = { target, host, inline: !!slot, x, y, width, recovery };
      setPosition(previous => previous && Object.entries(next).every(([key, value]) => previous[key as keyof Position] === value) ? previous : next);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
    measure();
    const observer = new MutationObserver(records => { if (records.some(record => !(record.target as Element).closest?.('[data-onboarding-guide]'))) schedule(); });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'aria-expanded', 'data-state'] });
    window.addEventListener('resize', schedule); document.addEventListener('scroll', schedule, true);
    document.addEventListener('transitionend', schedule, true); document.addEventListener('animationend', schedule, true);
    window.visualViewport?.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame); window.clearTimeout(settle); observer.disconnect(); highlighted?.removeAttribute('data-walkthrough-highlight');
      window.removeEventListener('resize', schedule); document.removeEventListener('scroll', schedule, true);
      document.removeEventListener('transitionend', schedule, true); document.removeEventListener('animationend', schedule, true);
      window.visualViewport?.removeEventListener('resize', schedule);
    };
  }, [task, index, step, height, location.pathname, isMobile]);

  const advance = () => {
    // Phones: leave a pushed screen (a team chat's info) when the next step is on the page under it.
    const page = isMobile ? position?.target?.closest<HTMLElement>('[role="dialog"][aria-modal="true"]') : null;
    const next = showStep(task.steps[index + 1], isMobile);
    setIndex(index + 1);
    if (page && next && !next.targets.some(name => page.querySelector(selectorFor(name)))) page.querySelector<HTMLElement>('button[aria-label^="Back"]')?.click();
    // 768-1279px: a details panel opened over the thread (team chat) covers the
    // next step's target, so close it.
    else if (task.id === 'team_chat' && !isMobile && innerWidth < 1280 && next?.targets.some(name => { const node = document.querySelector<HTMLElement>(selectorFor(name)); return !!node && isCovered(node); })) {
      document.querySelector<HTMLElement>('button[aria-label^="Close"][aria-label$=" details"]')?.click();
    }
  };

  if (!position) return null;
  return createPortal(<section ref={card} data-onboarding-guide className={`walkthrough-coach ${position.inline ? 'is-inline' : ''}`} style={position.inline ? undefined : { position: position.host === document.body ? 'fixed' : 'absolute', left: position.x, top: position.y, width: position.width }} aria-label="Dashboard guide">
    <button className="walkthrough-close" aria-label="End guide" disabled={busy} onClick={() => { void finish('dismiss'); }}><X size={18} /></button>
    <div className="walkthrough-eyebrow"><BookOpen size={16} />{task.title}</div>
    <div aria-live="polite"><h2>{position.recovery ? 'Let’s find the next step' : step.title}</h2><p>{position.recovery || body}</p></div>
    {error && <p role="alert" className="walkthrough-error">{error}</p>}
    {step.next && !position.recovery && <button className="walkthrough-primary" onClick={advance}>Continue</button>}
    {step.openDrawer && position.recovery && <button className="walkthrough-primary" onClick={() => setOpenMobile(true)}>Show me</button>}
    {step.final && !position.recovery && <button className="walkthrough-primary" disabled={busy} onClick={async () => { if (await finish('complete')) navigate('/walkthroughs'); }}>{busy ? 'Saving…' : <>Got it <Check size={16} /></>}</button>}
    <footer><span>{index + 1} of {task.steps.length}</span><button disabled={busy} onClick={() => { void finish('dismiss'); }}>End guide</button></footer>
  </section>, position.host);
}
